import { noteTeamArchived } from "src/Pages/KnowledgeBase/kbSession";
import { useEffect, useState } from "react";
import { vocabularize } from "src/Common/vocab";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";

import CancelButton from "src/Components/Common/cancelbutton";
import UserService from "src/Services/API/user.service";
import RoleService from "src/Services/API/role.service";
import BranchService from "src/Services/API/branch.service";
import TechnicianService from "src/Services/API/technician.service";
import ConfirmDialog from "src/Common/confirmdialog";
import InviteModal from "../Invite/InviteModal/invitemodal";
import InviteService from "src/Services/API/invite.service";
import RoleDropDown from "./roledropdown";
import BranchDropDown from "./branchdropdown";
import TechActivityDrawer from "../status/TechActivityDrawer";
import EditMemberDrawer from "./EditMemberDrawer";
import { setBranch } from "src/Redux/Slices/BranchSlice";
import { can, handleError, formatUSPhone, isSuperAdmin, isSuperAdminUser } from "src/Common/function";

function Manage({ branch }) {
  const navigate = useNavigate();
  const currentUser = UserService.getCurrentUser();
  const [userArray, setUserArray] = useState([]);
  const [index, setIndex] = useState(0);
  const [isDeleteConfirmDialogShown, setIsDeleteConfirmDialogShown] = useState(false);
  const [roleChangedUserArray, setRoleChangedUserArray] = useState([]);
  const [roleArray, setRoleArray] = useState([]);
  const [branchArray, setBranchArray] = useState([]);
  const [activityTech, setActivityTech] = useState(null);
  const [editMemberUser, setEditMemberUser] = useState(null);
  // user.id -> status board entry ({ techStatus, wo, user, ... }) so a clicked
  // row can show the same status label + Active Work Order card as the board.
  const [techByUserId, setTechByUserId] = useState({});

  useEffect(() => {
    console.log("🚀 ~ Manage ~ branch:", branch);
    getAllUsers();
    loadStatusBoard();
  }, [branch]);

  useEffect(() => {
    RoleService.getAll()
      .then((response) => {
        if (response.status == 200) setRoleArray(response.data);
      })
      .catch((error) => handleError(error));

    BranchService.getAll()
      .then((response) => {
        if (response.status == 200) setBranchArray(response.data);
      })
      .catch((error) => handleError(error));
  }, []);

  const getAllUsers = () => {
    UserService.getAll(branch)
      .then((response) => {
        if (response.status == 200) {
          // The backend already limits the response to the caller's company, so
          // this only narrows by branch.
          //
          // Branch membership is not always set: a company's first Administrator
          // is invited before any branch exists, so they belong to none. Such a
          // user is not branch-scoped rather than scoped to nothing — hiding
          // them made the Administrator invisible in Team, and a branch-less
          // viewer saw an empty list.
          const enabled = response.data.filter((user) => user.isEnabled === true);
          const branchesOf = (user) => (Array.isArray(user.branches) ? user.branches : []);
          let users;

          if (branch == 0) {
            // A Super Admin inside an entered tenant belongs to none of its
            // branches, so narrowing by their own (home-company) branches would
            // hide every member who has one. The company scope already confines
            // the list to that tenant.
            const viewerBranchIds = isSuperAdmin() ? [] : (currentUser?.branches || []).map((b) => b.id);
            users = viewerBranchIds.length
              ? enabled.filter(
                  (user) =>
                    branchesOf(user).length === 0 ||
                    branchesOf(user).some((b) => viewerBranchIds.includes(b.id)),
                )
              : enabled;
          } else {
            // A specific branch is selected: only its members, plus the
            // branch-less users, who would otherwise be unreachable.
            users = enabled.filter(
              (user) =>
                branchesOf(user).length === 0 || branchesOf(user).some((b) => b.id == branch),
            );
          }

          for (let i = 0; i < users.length; i++) {
            const date = new Date(users[i].createdAt);
            users[i].formattedCreatedAt = moment(date).format("MMM D, YYYY");
          }
          setUserArray(users);
        }
      })
      .catch((error) => {
        if (error.response) {
          toast(error.response.data.message);
        } else {
          toast("An error occured");
        }
      });
  };

  // Fetch the technician status board so a clicked user can surface their
  // current status + active work order in the activity drawer. Keyed by user id
  // because the drawer/status board nest the user under `tech.user`.
  const loadStatusBoard = () => {
    // The status board is gated by user:readStatus; skip the call (and its 403)
    // when the user can't read it.
    if (!can("user:readStatus")) return;
    TechnicianService.getStatusBoard(branch)
      .then((response) => {
        if (response.status === 200) {
          const map = {};
          (response.data || []).forEach((tech) => {
            if (tech.user?.id) map[tech.user.id] = tech;
          });
          setTechByUserId(map);
        }
      })
      .catch((error) => handleError(error));
  };

  const handleRoleChange = (user, role) => {
    if ((user.roles ?? []).some((r) => r.id === role.id)) {
      return;
    }
    // Assigning a role goes through the dedicated user:assignRole endpoint.
    persistUser(user, { roles: [role] }, "Role updated successfully", () => UserService.assignRole(user.id, [role]));
  };

  const handleBranchChange = (user, branchOption, checked) => {
    const currentBranches = user.branches ?? [];
    const nextBranches = checked
      ? [...currentBranches, branchOption]
      : currentBranches.filter((b) => b.id !== branchOption.id);
    if (nextBranches.length === 0 && !isSuperAdminUser(user)) {
      toast("User must belong to at least one branch");
      return;
    }
    // Assigning branches goes through the dedicated user:assignBranch endpoint.
    persistUser(user, { branches: nextBranches }, "Branches updated successfully", () =>
      UserService.assignBranches(user.id, nextBranches),
    );
  };

  // Optimistically applies `fields` to the row, then persists via `request`
  // (defaults to the user:update-gated staff partial-update endpoint).
  // Role/branch assignment pass their own dedicated-endpoint request.
  const persistUser = (
    user,
    fields,
    successMessage = "User updated successfully",
    request = () => UserService.updateUserPartialStaff(user.id, fields),
  ) => {
    setUserArray((prev) => prev.map((u) => (u.id === user.id ? { ...u, ...fields } : u)));
    request()
      .then((response) => {
        if (response.status == 201) {
          toast(successMessage);
          getAllUsers();
        }
      })
      .catch((error) => {
        handleError(error);
        getAllUsers();
      });
  };

  const deleteUser = () => {
    UserService.deleteUser(userArray[index].id)
      .then((response) => {
        if (response.status == 201) {
          noteTeamArchived();
          toast("User archived successfully");
          getAllUsers();
        }
        setIsDeleteConfirmDialogShown(false);
      })
      .catch((error) => {
        setIsDeleteConfirmDialogShown(false);

        if (error.response) {
          toast(error.response.data.message);
        } else {
          toast("An error occured");
        }
      });
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <TechActivityDrawer isOpen={!!activityTech} tech={activityTech} onClose={() => setActivityTech(null)} />
      <EditMemberDrawer
        isOpen={!!editMemberUser}
        user={editMemberUser}
        roles={roleArray}
        branches={branchArray}
        onClose={() => setEditMemberUser(null)}
        onSaved={getAllUsers}
      />
      <ConfirmDialog
        tour="team-archive"
        title="ARCHIVE USER"
        description="Are you sure you want to archive this user?"
        shown={isDeleteConfirmDialogShown}
        onConfirmClicked={deleteUser}
        onCancelClicked={() => setIsDeleteConfirmDialogShown(false)}
      />
      <div
        className="w-full mt-3 mb-3 flex-1 min-h-0 flex flex-col overflow-hidden"
        style={{
          borderRadius: "12px",
          border: "1px solid #E5E8F3",
          background: "#FFF",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.00), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        }}
      >
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto" data-tour="team-table">
          <table className="w-full min-w-[1240px] table-fixed">
            <thead className="sticky top-0 z-10">
              <tr style={{ borderBottom: "1px solid #E5E8F3", background: "#F8F9FE" }}>
                <th className="w-[70px]">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                    No.
                  </div>
                </th>
                <th>
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center gap-1 px-4 h-[44px] text-[#5F6477]">
                    <span>Name</span>
                    <span className="text-[10px]">↓</span>
                  </div>
                </th>
                <th className="w-[140px]">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                    Phone
                  </div>
                </th>
                <th className="w-[200px]">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                    Role
                  </div>
                </th>
                <th className="w-[360px]">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center px-4 h-[44px] text-[#5F6477]">
                    Branch
                  </div>
                </th>
                <th className="w-[160px]">
                  <div className="font-OpenSansRegular font-[600] text-[12px] leading-[18px] flex items-center gap-1 px-4 h-[44px] text-[#5F6477]">
                    <span>Added</span>
                    <span className="text-[10px]">↓</span>
                  </div>
                </th>
                <th className="w-[48px]"></th>
              </tr>
            </thead>
            <tbody>
              {userArray.map((user, i) => (
                <tr key={i} className="border-b border-borderColor hover:bg-[#FAFAFB]">
                  <td>
                    <div className="font-OpenSansRegular px-4 py-3 text-[12px] font-[400] text-[#717680]">{i + 1}</div>
                  </td>
                  <td>
                    {/* Clicking a name opens the activity drawer, which loads the
                        activity log — gated by user:readActivityLog. */}
                    <div
                      className={`px-4 py-3 flex flex-col ${can("user:readActivityLog") ? "cursor-pointer" : ""}`}
                      onClick={
                        can("user:readActivityLog")
                          ? () => setActivityTech(techByUserId[user.id] ?? { user })
                          : undefined
                      }
                    >
                      <span
                        className={`font-OpenSansRegular text-[14px] font-[600] leading-[20px] text-[#1A2332] ${
                          can("user:readActivityLog") ? "hover:underline" : ""
                        }`}
                      >
                        {user.name}
                      </span>
                      <span className="font-OpenSansRegular text-[14px] font-[400] leading-[20px] text-[#5F6477] truncate max-w-[300px]">
                        {user.email}
                      </span>
                    </div>
                  </td>
                  <td className="relative">
                    <div className="font-OpenSansRegular px-4 py-3 text-[14px] font-[400] leading-[20px] text-[#5F6477]">
                      {user.phone ? formatUSPhone(user.phone) : "—"}
                    </div>
                  </td>
                  <td className="relative">
                    <div className="px-2 py-3" data-tour={i === 0 ? "team-role" : undefined}>
                      {can("user:assignRole") ? (
                        <RoleDropDown user={user} roles={roleArray} onChange={handleRoleChange} />
                      ) : (
                        <div className="flex flex-wrap items-center gap-1">
                          {(user.roles ?? []).slice(0, 2).map((role) => (
                            <span
                              key={role.id}
                              className="flex justify-center items-center px-2 py-[2px] rounded-[6px] bg-[#EAF3FF] text-center text-[#0058E5] font-OpenSans text-[12px] font-semibold leading-[18px] tracking-[0.12px] capitalize"
                            >
                              {vocabularize(role.name)}
                            </span>
                          ))}
                          {(user.roles ?? []).length > 2 && (
                            <span className="font-OpenSansRegular text-[12px] text-[#535862] bg-[#F3F4F6] rounded-md px-2 py-1">
                              +{user.roles.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="relative">
                    <div className="px-2 py-3">
                      {/* A Super Admin reaches every branch of every tenant, so a
                          branch assignment would neither grant nor limit anything. */}
                      {isSuperAdminUser(user) ? (
                        <span className="flex w-fit items-center px-2 py-[2px] rounded-[6px] bg-[#EEF0F9] text-[#5F6477] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px]">
                          All branches
                        </span>
                      ) : can("user:assignBranch") ? (
                        <BranchDropDown user={user} branches={branchArray} onChange={handleBranchChange} />
                      ) : (
                        <div className="flex flex-wrap items-center gap-1">
                          {(user.branches ?? []).slice(0, 2).map((branch) => (
                            <span
                              key={branch.id}
                              className="flex flex-col justify-center items-center px-2 py-[2px] rounded-[6px] bg-[#EAF3FF] text-[#0058E5] font-OpenSans text-[12px] font-semibold leading-[18px] tracking-[0.12px] capitalize"
                            >
                              {branch.name}
                            </span>
                          ))}
                          {(user.branches ?? []).length > 2 && (
                            <span className="flex flex-col justify-center items-center px-2 py-[2px] rounded-[6px] bg-[#EAF3FF] text-[#0058E5] font-OpenSans text-[12px] font-semibold leading-[18px] tracking-[0.12px]">
                              +{user.branches.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="px-4 py-3 font-OpenSans text-[14px] font-normal leading-[20px] text-[#5F6477]">
                      {user.formattedCreatedAt}
                    </div>
                  </td>
                  <td>
                    <RowKebabMenu
                      first={i === 0}
                      onViewActivity={
                        can("user:readActivityLog")
                          ? () => setActivityTech(techByUserId[user.id] ?? { user })
                          : undefined
                      }
                      onEdit={
                        can("user:assignRole") || can("user:assignBranch")
                          ? () => setEditMemberUser(user)
                          : undefined
                      }
                      onArchive={
                        can("user:archive")
                          ? () => {
                              setIsDeleteConfirmDialogShown(true);
                              setIndex(i);
                            }
                          : undefined
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Manage;

const ViewActivityIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.53325 2.5C6.51484 2.5 6.49992 2.51492 6.49992 2.53333V3.5H9.49992V2.53333C9.49992 2.51492 9.485 2.5 9.46659 2.5H6.53325ZM10.4999 3.5V2.53333C10.4999 1.96264 10.0373 1.5 9.46659 1.5H6.53325C5.96256 1.5 5.49992 1.96264 5.49992 2.53333V3.5H2.93325C1.77345 3.5 0.833252 4.4402 0.833252 5.6V11.7333C0.833252 12.8931 1.77345 13.8333 2.93325 13.8333H13.0666C14.2264 13.8333 15.1666 12.8931 15.1666 11.7333V5.6C15.1666 4.4402 14.2264 3.5 13.0666 3.5H10.4999ZM2.93325 4.5C2.32574 4.5 1.83325 4.99249 1.83325 5.6V8.16667H6.16659V7.86667C6.16659 7.29597 6.62923 6.83333 7.19992 6.83333H8.79992C9.37061 6.83333 9.83325 7.29597 9.83325 7.86667V8.16667H14.1666V5.6C14.1666 4.99249 13.6741 4.5 13.0666 4.5H2.93325ZM14.1666 9.16667H9.83325V9.46667C9.83325 10.0374 9.37061 10.5 8.79992 10.5H7.19992C6.62923 10.5 6.16659 10.0374 6.16659 9.46667V9.16667H1.83325V11.7333C1.83325 12.3408 2.32574 12.8333 2.93325 12.8333H13.0666C13.6741 12.8333 14.1666 12.3408 14.1666 11.7333V9.16667ZM7.16659 9.46667C7.16659 9.48508 7.18151 9.5 7.19992 9.5H8.79992C8.81833 9.5 8.83325 9.48508 8.83325 9.46667V7.86667C8.83325 7.84826 8.81833 7.83333 8.79992 7.83333H7.19992C7.18151 7.83333 7.16659 7.84826 7.16659 7.86667V9.46667Z"
      fill="#7C829A"
    />
  </svg>
);

const EditMemberIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 15 15" fill="none" className="shrink-0">
    <path
      d="M13.3333 2.37956C13.3333 2.01376 13.1876 1.66299 12.929 1.4043C12.6704 1.14571 12.32 1.00012 11.9544 1C11.5887 0.999954 11.2378 1.1452 10.9791 1.40365L2.08131 10.3034C1.98494 10.3995 1.91246 10.5178 1.87233 10.6478L1.87298 10.6484L1.08261 13.2494L3.68548 12.4603C3.81596 12.4206 3.93524 12.3495 4.03183 12.2533L12.929 3.35482C13.1876 3.09622 13.3332 2.74531 13.3333 2.37956ZM14.3333 2.37956C14.3332 2.9712 14.1127 3.54015 13.7174 3.97656L13.636 4.06185L4.73756 12.9616C4.55164 13.1469 4.32846 13.2902 4.08327 13.3815L3.97649 13.4173L1.07415 14.2975H1.07285C0.928861 14.3408 0.775939 14.3446 0.63014 14.3079C0.484247 14.2713 0.351184 14.1955 0.244723 14.0892C0.138315 13.983 0.0622548 13.8502 0.0253219 13.7044C-0.0115909 13.5586 -0.0079871 13.4052 0.0350875 13.2611L0.0357386 13.2591L0.915947 10.3574L0.916598 10.3561C1.00471 10.0689 1.16218 9.80785 1.37493 9.59571L10.2714 0.696615C10.7177 0.250474 11.3233 -7.90965e-05 11.9544 1.87304e-08C12.5853 0.00012496 13.1906 0.251054 13.6367 0.697266C14.0827 1.1435 14.3333 1.74862 14.3333 2.37956Z"
      fill="#7C829A"
    />
  </svg>
);

const ArchiveMemberIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 15 13" fill="none" className="shrink-0">
    <path
      d="M13.3333 1.16667C13.3333 1.07462 13.2587 1 13.1667 1H1.16667C1.07462 1 1 1.07462 1 1.16667V3.16667C1 3.25871 1.07462 3.33333 1.16667 3.33333H13.1667C13.2587 3.33333 13.3333 3.25871 13.3333 3.16667V1.16667ZM14.3333 3.16667C14.3333 3.811 13.811 4.33333 13.1667 4.33333H1.16667C0.522335 4.33333 0 3.811 0 3.16667V1.16667C0 0.522335 0.522335 0 1.16667 0H13.1667C13.811 0 14.3333 0.522335 14.3333 1.16667V3.16667Z"
      fill="#7C829A"
    />
    <path
      d="M1.33333 11.1667V3.83333C1.33333 3.55719 1.55719 3.33333 1.83333 3.33333C2.10948 3.33333 2.33333 3.55719 2.33333 3.83333V11.1667C2.33333 11.3877 2.42119 11.5996 2.57747 11.7559C2.73376 11.9121 2.94565 12 3.16667 12H11.1667C11.3877 12 11.5996 11.9121 11.7559 11.7559C11.9121 11.5996 12 11.3877 12 11.1667V3.83333C12 3.55719 12.2239 3.33333 12.5 3.33333C12.7761 3.33333 13 3.55719 13 3.83333V11.1667C13 11.6529 12.8067 12.1191 12.4629 12.4629C12.1191 12.8067 11.6529 13 11.1667 13H3.16667C2.68044 13 2.21426 12.8067 1.87044 12.4629C1.52663 12.1191 1.33333 11.6529 1.33333 11.1667Z"
      fill="#7C829A"
    />
    <path
      d="M8.5 6C8.77614 6 9 6.22386 9 6.5C9 6.77614 8.77614 7 8.5 7H5.83333C5.55719 7 5.33333 6.77614 5.33333 6.5C5.33333 6.22386 5.55719 6 5.83333 6H8.5Z"
      fill="#7C829A"
    />
  </svg>
);

// first: the first row carries the Knowledge Base walkthrough anchors (Azure 416).
const RowKebabMenu = ({ onViewActivity, onEdit, onArchive, first }) => {
  const [open, setOpen] = useState(false);
  const items = [
    onViewActivity && { label: "View activity", icon: <ViewActivityIcon />, onClick: onViewActivity },
    onEdit && { label: "Edit member", icon: <EditMemberIcon />, onClick: onEdit },
    onArchive && { label: "Archive member", icon: <ArchiveMemberIcon />, onClick: onArchive },
  ].filter(Boolean);

  if (items.length === 0) return null;

  return (
    <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
      <div className="relative flex items-center justify-center">
        <button
          data-tour={first ? "team-row-menu" : undefined}
          onClick={() => setOpen((v) => !v)}
          className={`w-8 h-8 flex items-center justify-center rounded-[8px] cursor-pointer transition-colors ${
            open ? "bg-[#EEF0F7] text-[#1A2332]" : "text-[#868DA6] hover:bg-[#EEF0F7] hover:text-[#1A2332]"
          }`}
          aria-label="Row actions"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="3" r="1.4" fill="currentColor" />
            <circle cx="8" cy="8" r="1.4" fill="currentColor" />
            <circle cx="8" cy="13" r="1.4" fill="currentColor" />
          </svg>
        </button>
        {open && (
          <div data-tour={first ? "team-row-menu-list" : undefined} className="absolute right-0 top-9 z-20 w-[200px] bg-white rounded-[10px] outline outline-1 -outline-offset-1 outline-[#E5E8F3] shadow-[0px_8px_24px_rgba(16,24,40,0.12)] p-1 flex flex-col gap-0.5">
            {items.map((item) => (
              <button
                key={item.label}
                onClick={() => {
                  setOpen(false);
                  item.onClick?.();
                }}
                className="w-full min-h-[36px] flex items-center gap-3 px-3 py-1.5 rounded-[8px] text-left text-[14px] leading-5 tracking-[0.14px] capitalize font-OpenSansSemiBold text-[#1A2332] hover:bg-[#F5F9FF]"
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
};

